import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, ShieldAlert, Grid3x3 } from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip, ScatterChart, Scatter, XAxis, YAxis,
  CartesianGrid, ReferenceLine, ReferenceArea, ZAxis,
} from 'recharts';

/**
 * Module Health Heatmap — cell-per-module coloured by combined health
 * (schedule status × cost status × risk exposure).
 */
export function ModuleHealthHeatmap({ data }) {
  const cells = useMemo(() => {
    const kpis = data || {};
    const clamp = (n, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, Number(n) || 0));
    // Derive a 0-100 health score per module (higher = better)
    const spi = clamp(((kpis.schedule?.spi || 1) * 100), 0, 130);
    const cpi = clamp(((kpis.costEvm?.cpi || 1) * 100), 0, 130);
    const eng = clamp(kpis.engineering?.kpis?.progress_pct);
    const proc = clamp(kpis.procurement?.kpis?.progress_pct);
    const cons = clamp(kpis.construction?.kpis?.actual_pct);
    const hse  = clamp(100 - (kpis.hse?.incidents?.length || 0) * 3);
    const raid = clamp(100 - (kpis.raid?.kpis?.high_priority || 0) * 8);
    const manp = clamp(kpis.resources?.kpis?.manpower_utilization);
    const equp = clamp(kpis.resources?.kpis?.equipment_utilization);
    const cont = clamp((kpis.contracts?.kpis?.on_time_delivery_rate || 60));
    const pwm  = clamp(kpis.pwm?.progressPct || (kpis.pwm?.lines ? 40 : 30));
    const swm  = clamp(kpis.swm?.progressPct || 35);
    const items = [
      { key: 'SCH',  label: 'Schedule',    value: spi },
      { key: 'CST',  label: 'Cost',        value: cpi },
      { key: 'ENG',  label: 'Engineering', value: eng },
      { key: 'PRC',  label: 'Procurement', value: proc },
      { key: 'CON',  label: 'Construction',value: cons },
      { key: 'HSE',  label: 'HSE',         value: hse },
      { key: 'RSK',  label: 'RAID',        value: raid },
      { key: 'MAN',  label: 'Manpower',    value: manp },
      { key: 'EQP',  label: 'Equipment',   value: equp },
      { key: 'CLM',  label: 'Contracts',   value: cont },
      { key: 'PWM',  label: 'Piping',      value: pwm },
      { key: 'SWM',  label: 'Structural',  value: swm },
    ];
    return items;
  }, [data]);

  const colFor = (v) => {
    if (v >= 85) return { bg: 'rgba(16,185,129,0.85)', text: '#022c22' };
    if (v >= 70) return { bg: 'rgba(34,197,94,0.65)',  text: '#052e16' };
    if (v >= 55) return { bg: 'rgba(245,158,11,0.65)', text: '#451a03' };
    if (v >= 40) return { bg: 'rgba(249,115,22,0.7)',  text: '#431407' };
    return          { bg: 'rgba(239,68,68,0.85)',      text: '#450a0a' };
  };

  return (
    <Card className="bg-white/70 dark:bg-[#1F2937] border-slate-200 dark:border-[#3A3F4A]"
          data-testid="module-health-heatmap">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <Grid3x3 className="w-4 h-4 text-emerald-500" />
          Module Health Heatmap
          <span className="ml-auto text-[10px] font-normal text-slate-400">Higher = healthier</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
          {cells.map(c => {
            const { bg, text } = colFor(c.value);
            return (
              <div key={c.key}
                   className="rounded-lg p-2.5 h-24 flex flex-col justify-between border border-black/5"
                   style={{ background: bg, color: text }}
                   title={`${c.label}: ${Math.round(c.value)}`}>
                <div className="text-[10px] font-mono opacity-80">{c.key}</div>
                <div>
                  <div className="text-[10px] leading-tight font-medium">{c.label}</div>
                  <div className="text-lg font-bold tabular-nums">{Math.round(c.value)}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-500">
          <span className="w-3 h-2 rounded" style={{ background: 'rgba(239,68,68,0.85)' }} />&lt;40
          <span className="w-3 h-2 rounded" style={{ background: 'rgba(249,115,22,0.7)' }} />40-55
          <span className="w-3 h-2 rounded" style={{ background: 'rgba(245,158,11,0.65)' }} />55-70
          <span className="w-3 h-2 rounded" style={{ background: 'rgba(34,197,94,0.65)' }} />70-85
          <span className="w-3 h-2 rounded" style={{ background: 'rgba(16,185,129,0.85)' }} />85+
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Cross-Module Risk Radar — polar chart of risk exposure per domain.
 */
export function CrossModuleRiskRadar({ raidData, crossModuleRisks }) {
  const data = useMemo(() => {
    // RAID endpoint returns `items` + `risks` + `issues` etc.; the schema uses
    // `discipline` (e.g., "Engineering") or `raidCategory` — no unified `domain` field
    const items = raidData?.items || raidData?.raidItems
                  || [...(raidData?.risks || []), ...(raidData?.issues || []),
                      ...(raidData?.actions || []), ...(raidData?.decisions || [])];
    const bucket = {
      'Schedule': 0, 'Cost': 0, 'Quality': 0, 'HSE': 0,
      'Engineering': 0, 'Procurement': 0, 'Contracts': 0, 'Interface': 0,
    };
    const SEV_W = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
    // Map string → canonical axis
    const AXIS_MAP = {
      SCHEDULE: 'Schedule', COST: 'Cost', BUDGET: 'Cost', QUALITY: 'Quality',
      SAFETY: 'HSE', HSE: 'HSE', ENVIRONMENT: 'HSE',
      ENGINEERING: 'Engineering', DESIGN: 'Engineering',
      PROCUREMENT: 'Procurement', SUPPLY: 'Procurement', MATERIAL: 'Procurement',
      CONTRACTUAL: 'Contracts', CONTRACT: 'Contracts', COMMERCIAL: 'Contracts',
      INTERFACE: 'Interface', COORDINATION: 'Interface',
      CONSTRUCTION: 'Schedule', OPERATIONAL: 'Schedule',
      REGULATORY: 'Contracts',
    };
    for (const it of items) {
      const raw = String(it.domain || it.raidCategory || it.discipline || it.category || 'SCHEDULE').toUpperCase();
      const axis = AXIS_MAP[raw] || 'Schedule';
      bucket[axis] = (bucket[axis] || 0) + (SEV_W[(it.priority || it.severity || 'MEDIUM').toUpperCase()] || 2);
    }
    for (const cr of (crossModuleRisks?.risks || [])) {
      const axis = AXIS_MAP[String(cr.affected_module || cr.domain || 'ENGINEERING').toUpperCase()] || 'Engineering';
      bucket[axis] = (bucket[axis] || 0) + 2;
    }
    return Object.entries(bucket).map(([axis, exposure]) => ({ axis, exposure }));
  }, [raidData, crossModuleRisks]);

  const totalExposure = data.reduce((s, d) => s + d.exposure, 0);
  const maxV = Math.max(...data.map(d => d.exposure), 10);

  return (
    <Card className="bg-white/70 dark:bg-[#1F2937] border-slate-200 dark:border-[#3A3F4A]"
          data-testid="cross-module-risk-radar">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-orange-500" />
          Cross-Module Risk Radar
          <span className="ml-auto text-[10px] font-normal text-slate-400">
            {totalExposure > 0 ? `Total exposure ${totalExposure}` : 'No exposure yet'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-2 leading-relaxed">
          Weighted RAID exposure across 8 project domains. Every open item contributes points
          based on severity (Critical 4 · High 3 · Medium 2 · Low 1). <b>Longer spikes</b> = more concentrated
          risk in that domain. Use it to see where risk is <b>piling up</b> so mitigation attention
          can be focused there first.
        </p>
        <ResponsiveContainer width="100%" height={260}>
          <RadarChart data={data} outerRadius={90}>
            <PolarGrid stroke="#94a3b8" strokeOpacity={0.3} />
            <PolarAngleAxis dataKey="axis" tick={{ fill: '#64748b', fontSize: 10 }} />
            <PolarRadiusAxis angle={90} domain={[0, maxV]} tick={{ fill: '#94a3b8', fontSize: 9 }} />
            <Radar name="Exposure" dataKey="exposure" stroke="#f97316" fill="#f97316" fillOpacity={0.35} />
            <Tooltip
              contentStyle={{ background: '#0A0E1A', border: '1px solid #3A3F4A', borderRadius: 6, fontSize: 11 }}
              labelStyle={{ color: '#e2e8f0' }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

/**
 * Cost vs Schedule Performance Matrix — SPI on X, CPI on Y, size = BAC.
 * Divides into Green (both>=1), Amber (one<1), Red (both<1) quadrants.
 */
export function CostVsScheduleMatrix({ schedule, costEvm }) {
  // Support both flat and nested (kpis.*) shapes
  const spi = Number(schedule?.spi ?? schedule?.kpis?.spi ?? 1);
  const cpi = Number(costEvm?.cpi ?? costEvm?.kpis?.cpi ?? 1);
  const bac = Number(costEvm?.bac ?? costEvm?.kpis?.bac ?? 0);
  const point = [{ x: spi, y: cpi, z: bac || 1000000000, name: 'Project' }];

  const zone = spi >= 1 && cpi >= 1 ? 'HEALTHY'
             : spi < 1 && cpi < 1  ? 'CRITICAL'
             : 'AT-RISK';
  const zoneColor = { HEALTHY: '#10b981', 'AT-RISK': '#f59e0b', CRITICAL: '#dc2626' }[zone];

  return (
    <Card className="bg-white/70 dark:bg-[#1F2937] border-slate-200 dark:border-[#3A3F4A]"
          data-testid="cost-vs-schedule-matrix">
      <CardHeader className="pb-2">
        <CardTitle className="text-slate-800 dark:text-white text-sm flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-500" />
          Cost × Schedule Performance
          <span className="ml-auto px-2 py-0.5 rounded text-[10px] font-bold"
                style={{ background: zoneColor + '22', color: zoneColor }}>
            {zone}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" strokeOpacity={0.25} />
            {/* Quadrant colouring */}
            <ReferenceArea x1={0.6} x2={1} y1={0.6} y2={1} fill="#dc2626" fillOpacity={0.08} />
            <ReferenceArea x1={1}   x2={1.4} y1={0.6} y2={1} fill="#f59e0b" fillOpacity={0.08} />
            <ReferenceArea x1={0.6} x2={1}   y1={1}   y2={1.4} fill="#f59e0b" fillOpacity={0.08} />
            <ReferenceArea x1={1}   x2={1.4} y1={1}   y2={1.4} fill="#10b981" fillOpacity={0.08} />
            <ReferenceLine x={1} stroke="#94a3b8" strokeDasharray="4 3" />
            <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="4 3" />
            <XAxis type="number" dataKey="x" name="SPI" domain={[0.6, 1.4]}
                   tick={{ fill: '#64748b', fontSize: 10 }}
                   label={{ value: 'SPI →', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 10 }} />
            <YAxis type="number" dataKey="y" name="CPI" domain={[0.6, 1.4]}
                   tick={{ fill: '#64748b', fontSize: 10 }}
                   label={{ value: 'CPI ↑', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
            <ZAxis type="number" dataKey="z" range={[400, 1200]} name="BAC" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }}
                     contentStyle={{ background: '#0A0E1A', border: '1px solid #3A3F4A', borderRadius: 6, fontSize: 11, color: '#e2e8f0' }}
                     itemStyle={{ color: '#e2e8f0' }}
                     labelStyle={{ color: '#94a3b8', fontWeight: 600 }}
                     content={({ active, payload }) => {
                       if (!active || !payload?.length) return null;
                       const d = payload[0]?.payload || {};
                       return (
                         <div style={{ background: '#0A0E1A', border: '1px solid #3A3F4A', borderRadius: 6, padding: '8px 10px', fontSize: 11 }}>
                           <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>{d.name || 'Project'}</div>
                           <div style={{ color: d.x >= 1 ? '#10b981' : '#dc2626' }}>SPI: {Number(d.x).toFixed(2)}</div>
                           <div style={{ color: d.y >= 1 ? '#10b981' : '#dc2626' }}>CPI: {Number(d.y).toFixed(2)}</div>
                           <div style={{ color: '#e2e8f0' }}>BAC: ₹{(Number(d.z)/1e7).toFixed(1)} Cr</div>
                           <div style={{ color: zoneColor, marginTop: 2, fontWeight: 600 }}>Zone: {zone}</div>
                         </div>
                       );
                     }} />
            <Scatter name="Project" data={point} fill={zoneColor} />
          </ScatterChart>
        </ResponsiveContainer>
        <div className="text-[10px] text-slate-500 flex items-center justify-center gap-3 mt-1">
          <span>SPI: <b style={{ color: spi >= 1 ? '#10b981' : '#dc2626' }}>{spi.toFixed(2)}</b></span>
          <span>CPI: <b style={{ color: cpi >= 1 ? '#10b981' : '#dc2626' }}>{cpi.toFixed(2)}</b></span>
          <span>BAC: <b>₹{(bac/1e7).toFixed(1)} Cr</b></span>
        </div>
      </CardContent>
    </Card>
  );
}
